"use client";

import { useState, useEffect } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface Product {
  id: string;
  name: string;
  category: string;
  purity: string;
  origin: string;
  image: string;
  alt: string;
  badge: string;
}

export default function FeaturedProductsCarousel() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const products: Product[] = [
  {
    id: "prod_gold_nugget",
    name: "Gold Nuggets",
    category: "Precious Metal",
    purity: "99.9% Pure",
    origin: "Mubende Region",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c85be38e-1768333363133.png",
    alt: "Natural gold nuggets with bright metallic golden yellow color and irregular crystalline surface texture",
    badge: "Featured"
  },
  {
    id: "prod_cobalt_ore",
    name: "Cobalt Concentrate",
    category: "Industrial Mineral",
    purity: "20-25% Co",
    origin: "Kasese District",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1478c2142-1767956192965.png",
    alt: "Cobalt ore concentrate with deep blue metallic coloration and fine powder texture in industrial container",
    badge: "High Demand"
  },
  {
    id: "prod_copper_ore",
    name: "Copper Ore",
    category: "Base Metal",
    purity: "15-18% Cu",
    origin: "Kilembe Mines",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15a2cb2c6-1767060268769.png",
    alt: "Copper ore specimen with characteristic reddish-brown color and green oxidation patina on rough surface",
    badge: "Best Seller"
  },
  {
    id: "prod_tantalum",
    name: "Tantalum Ore",
    category: "Rare Earth",
    purity: "30-40% Ta2O5",
    origin: "Western Uganda",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_140ef230a-1766846622994.png",
    alt: "Tantalum mineral ore with dark metallic gray appearance and dense crystalline structure",
    badge: "Premium"
  }];


  const nextSlide = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev + 1) % products.length);
  };

  const prevSlide = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => (prev - 1 + products.length) % products.length);
  };

  return (
    <section className="py-24 bg-muted/30 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-8">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-[2px] h-4 bg-gradient-to-b from-golden to-silver"></div>
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-transparent bg-clip-text bg-gradient-to-r from-golden to-silver font-mono">
                Featured Products
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight font-heading">
              Premium Quality<br />
              Mineral Selection
            </h2>
          </div>

          {/* Navigation Buttons */}
          {isHydrated &&
          <div className="flex gap-3">
              <button
              onClick={prevSlide}
              className="w-12 h-12 rounded-full border-2 border-golden/30 bg-card flex items-center justify-center hover:bg-gradient-to-br hover:from-golden hover:to-silver hover:text-foreground hover:border-golden transition-all shadow-lg"
              aria-label="Previous product">
              
                <Icon name="ChevronLeftIcon" size={20} />
              </button>
              <button
              onClick={nextSlide}
              className="w-12 h-12 rounded-full border-2 border-golden bg-gradient-to-br from-golden to-silver text-foreground flex items-center justify-center hover:scale-110 transition-all shadow-lg shadow-golden/50"
              aria-label="Next product">
              
                <Icon name="ChevronRightIcon" size={20} />
              </button>
            </div>
          }
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="flex gap-6 overflow-x-auto custom-scrollbar pb-4 snap-x snap-mandatory">
            {products.map((product, index) =>
            <div
              key={product.id}
              className={`flex-shrink-0 w-[300px] md:w-[350px] snap-start transition-all duration-500 ${
              isHydrated && index === currentIndex ?
              "scale-100 opacity-100" : "scale-95 opacity-70"}`
              }>
              
                <div className="bg-card border-2 border-border rounded-2xl overflow-hidden hover:border-golden/50 hover:shadow-2xl hover:shadow-golden/20 transition-all duration-500 group h-full">
                  <div className="relative h-64 overflow-hidden">
                    <AppImage
                    src={product.image}
                    alt={product.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-golden to-silver text-foreground px-3 py-1 text-[9px] uppercase tracking-wider font-mono shadow-lg">
                      {product.badge}
                    </div>
                  </div>

                  <div className="p-6">
                    <span className="text-[9px] uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-golden to-silver font-mono">
                      {product.category}
                    </span>
                    <h3 className="text-xl font-semibold text-foreground mt-2 mb-4 font-heading group-hover:text-golden transition-colors">
                      {product.name}
                    </h3>

                    <div className="space-y-2 mb-6">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground font-sans">
                          Purity:
                        </span>
                        <span className="text-foreground font-medium font-mono">
                          {product.purity}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground font-sans">
                          Origin:
                        </span>
                        <span className="text-foreground font-medium font-mono">
                          {product.origin}
                        </span>
                      </div>
                    </div>

                    <button className="w-full bg-gradient-to-r from-golden/10 via-silver/10 to-golden/10 text-golden border-2 border-golden/30 py-3 rounded-lg text-sm font-semibold uppercase tracking-wider hover:bg-gradient-to-r hover:from-golden hover:to-silver hover:text-foreground hover:border-golden transition-all font-mono shadow-lg">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}